/**
 * clientLog — browser-side LivePerson FAILURE telemetry (TRAC-732).
 *
 * Some connect failures never reach a backend handler: in the extension's
 * cross-origin / third-party iframe, the connect fetch can be blocked (CORS),
 * aborted (iframe torn down), or the response can fail to make it back into the
 * frame's code — even though the backend started the handoff fine. When that
 * happens the frontend shows "We couldn't reach a person just now" and, until
 * now, discarded the reason.
 *
 * `logLpFailure` captures that reason PLUS the browser context (is this a
 * cross-origin/third-party iframe? is storage access granted?) and forwards it
 * to the backend, where it lands in Datadog under the single marker `[LP_FAIL]`.
 *
 * Fire-and-forget. Never throws, never blocks the UI. Sends ONLY metadata —
 * reasons, HTTP statuses, error names/messages, timings, and context booleans.
 * NEVER message text, transcript, or customer identity.
 */
import chatAgentService from '../services/RealChatAgentService';

export function logLpFailure(event: string, fields: Record<string, unknown>): void {
  // Run async so we can best-effort probe storage access without blocking the caller.
  void (async () => {
    // Is the chat running inside a cross-origin (third-party) frame — i.e. the extension embed?
    let crossOriginParent = false;
    let topOriginKnown = false;
    try {
      topOriginKnown = !!window.top?.location?.origin;
    } catch {
      // Reading top.location.origin throws only across an origin boundary.
      crossOriginParent = true;
    }

    // Third-party storage can be partitioned/denied in an embedded iframe — best-effort probe.
    let hasStorageAccess: boolean | null = null;
    try {
      const doc = document as Document & { hasStorageAccess?: () => Promise<boolean> };
      if (typeof doc.hasStorageAccess === 'function') {
        hasStorageAccess = await doc.hasStorageAccess();
      }
    } catch {
      /* not supported / not applicable */
    }

    const detail: Record<string, unknown> = {
      ...fields,
      inIframe: window.top !== window.self,
      crossOriginParent,
      topOriginKnown,
      hasStorageAccess,
      referrer: document.referrer || null,
      ua: navigator.userAgent,
    };

    try {
      // eslint-disable-next-line no-console
      console.error('[LP_FAIL]', event, detail);
    } catch {
      /* ignore */
    }

    try {
      await chatAgentService.postClientLog({
        event,
        reason: (fields.reason as string) ?? null,
        detail,
        sessionId: (fields.sessionId as string) ?? null,
        level: 'error',
      });
    } catch {
      /* fire-and-forget */
    }
  })();
}
